import { useEffect, useState } from 'react';
import { IntersectionObserverWrapper } from '../dom-apis/intersectionObserver';

export function useFirstVisibleElement(
  observedElements: () => Element[],
  visibleElementCallback: (firstVisibleElement: Element | null) => void
) {
  const [firstVisibleElement, setFirstVisibleElement] = useState<Element | null>(null);

  useEffect(() => {
    visibleElementCallback(firstVisibleElement);
  }, [visibleElementCallback, firstVisibleElement]);

  useEffect(() => {
    const visibilityByElement = new Map<Element, boolean>();

    function manageVisibility(entries: IntersectionObserverEntry[]) {
      entries.forEach(entry => {
        visibilityByElement.set(entry.target, entry.isIntersecting);
      });
    }

    function manageFirstVisibleElement() {
      const visibleElements = Array.from(visibilityByElement.entries())
        .filter(([, value]) => value)
        .map(([key]) => key);

      setFirstVisibleElement(visibleElements[0] ?? null);
    }

    const observer = new IntersectionObserverWrapper(
      (entries: IntersectionObserverEntry[]) => {
        manageVisibility(entries);
        manageFirstVisibleElement();
      },
      {
        rootMargin: '0px 0px 0px 0px',
        threshold: [0.0, 0.7]
      }
    );

    observedElements().forEach(element => {
      visibilityByElement.set(element, false);
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [observedElements, visibleElementCallback]);
}
